import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Edit,
  AlertCircle,
  CheckCircle2,
  CheckCircle,
  Clock,
  FileText,
  Download,
  Loader2,
  Cross,
  ShieldCloseIcon,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { useSelector } from "react-redux";
import FieldInspectionForm from "../../../components/district_admin/FieldInspectionForm";
import api from "@/utils/api";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import ProvisionalSanctionForm from "../../../components/district_admin/ProvisionalSanctionForm";
import StatusBadge from "../../../components/common/StatusBadge";
import DynamicReviewTable from "../../../components/common/DynamicReviewTable";
import ReviewSection from "../../../components/common/ReviewSection";
import FileReviewSection from "../../../components/common/FileReviewSection";

const NewShuttlelessDetails = ({ application, view_page = true }) => {
  const [data, setData] = useState();
  const role = useSelector((state) => state.auth.user?.role) || "applicant";
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [adminApproveModalOpen, setAdminApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [approveFile, setApproveFile] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [provisionalSanctionAmount, setProvisionalSanctionAmount] = useState(
    application?.sanction_details?.sanction_order_amount
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const uploads_array = [
    { name: "Profile Photo", value: "photo" },
    { name: "Aadhaar Copy", value: "aadhaar_copy" },
    { name: "PAN Copy", value: "pan_upload" },
    { name: "GST Copy", value: "gst_upload" },
    { name: "Incorporation Certificate", value: "incorporation_certificate" },
    { name: "Partnership Deed", value: "partnership_deed" },
    { name: "Udyog Aadhaar", value: "udyog_aadhaar_copy" },
    { name: "SSI/MSME Registration", value: "ssi_copy" },
    { name: "Electricity Bill", value: "electricity_bill_attachment" },
    { name: "GPS Location", value: "geo_tag" },
    { name: "Purchase Proof", value: "purchase_proof" },
    { name: "Bill of Lading", value: "bill_of_lading" },
    { name: "Bill of Entry", value: "bill_of_entry" },
    { name: "DPR Copy", value: "dpr_copy" },
    { name: "Investment Proof", value: "investment_proof" },
    { name: "Loan Account Copy", value: "loan_account_copy" },
    { name: "Bank Passbook", value: "bank_passbook" },
    { name: "Export Oriented Copy", value: "export_oriented_copy" },
    { name: "Marketing Tieup Details", value: "marketing_tieup_details" },
    { name: "Turnover Proof", value: "turnover_proof" },
    { name: "Applicant Signature", value: "applicant_signature" },
    { name: "Declaration Document", value: "declaration_document" },
    { name: "Rental/Lease Agreement", value: "rental_lease_upload" },
    { name: "Other Valid Documents", value: "other_valid_documents" },
    { name: "Scheme Documents", value: "scheme_documents" },
    { name: "Opt Out Declaration", value: "opt_out_declaration_copy" },
  ];

  const tabsOrder = [
    { id: "personal", name: "Personal Details" },
    { id: "loom", name: "Beneficiary and Loom Details" },
    { id: "bank", name: "Bank Details" },
    { id: "priority", name: "Additional Details" },
    { id: "purchase_detail", name: "Purchase Details" },
    { id: "declaration", name: "Declaration" },
    { id: "all_documents", name: "All Uploaded Documents" },
    {
      id: "m_panel_list",
      name: "Verified with M-Panel supplier List's (13 supplier from G.O.I and List Approved by D.o.H",
    },
  ];

  useEffect(() => {
    setData({ ...application });
  }, [application]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-IN");
    } catch {
      return "-";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0.00";
    return `₹${parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
    })}`;
  };

  const openApproveModal = () => {
    if (role == "admin") {
      setAdminApproveModalOpen(true);
    } else {
      setApproveModalOpen(true);
    }
  };

  const openRejectModal = () => {
    setRejectModalOpen(true);
  };

  const handleApprove = async () => {
    try {
      setIsSubmitting(true);
      let response;

      if (role == "admin") {
        const formData = new FormData();
        formData.append("id", data?.id);
        formData.append("remarks", remarks);
        formData.append("sanction_order_amount", provisionalSanctionAmount);

        if (approveFile) {
          formData.append("approve_file", approveFile);
        }

        response = await api.post(
          "new_shuttleless/committee/approve",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      } else {
        response = await api.post("new_shuttleless/district-admin/approve", {
          id: data?.id,
          remarks: remarks,
        });
      }

      setData(response?.data?.data);
      toast({
        title: "Approved",
        description: "Submission approved successfully",
        variant: "success",
      });
      setApproveModalOpen(false);
      setAdminApproveModalOpen(false);
      setRemarks("");
      setProvisionalSanctionAmount("");
    } catch (error) {
      toast({
        title: "Failed",
        description: error.response?.data?.message || "Approval failed",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    try {
      setIsSubmitting(true);
      const submitData = new FormData();
      submitData.append("remarks", remarks);
      submitData.append("form_id", data?.id);
      let response;
      if (role == "admin") {
        response = await api.post(
          "new_shuttleless/committee/reject",
          submitData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      } else {
        response = await api.post(
          "new_shuttleless/district-admin/reject",
          submitData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      }

      setData(response?.data?.data);

      toast({
        title: "Rejected",
        description: "Submission rejected successfully",
        variant: "destructive",
      });
      setRejectModalOpen(false);
      setRemarks("");
    } catch (error) {
      toast({
        title: "Failed",
        description: error.response?.data?.message || "Rejection failed",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFileLink = (url, label = "View Document") => {
    if (!url || url.includes("tmp\\") || url === "null" || url === "undefined")
      return "-";
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        {label}
      </a>
    );
  };

  const afterSanctionTabsOrder = [
    { id: "sanction_details", name: "Sanction Details" },
    { id: "after_purchase_details", name: "After Purchase Details" },
    { id: "purchase_declaration", name: "Purchase Declaration Details" },
    { id: "all_documents", name: "All Uploaded Documents" },
  ];

  const uploadedFiles = [
    {
      label: "Applicant Photo",
      value: renderFileLink(data?.photo, "View Photo"),
    },
    {
      label: "Aadhaar Copy",
      value: renderFileLink(data?.aadhaar_copy),
    },
    {
      label: "PAN Copy",
      value: renderFileLink(data?.pan_upload),
    },
    {
      label: "GST Copy",
      value: renderFileLink(data?.gst_upload),
    },
    {
      label: "Incorporation Certificate",
      value: renderFileLink(data?.incorporation_certificate),
    },
    {
      label: "Partnership Deed",
      value: renderFileLink(data?.partnership_deed),
    },
    {
      label: "Udyog Aadhaar Copy",
      value: renderFileLink(data?.beneficiary?.udyog_aadhaar_copy),
    },
    {
      label: "SSI/MSME Registration",
      value: renderFileLink(data?.beneficiary?.ssi_copy),
    },
    {
      label: "Electricity Bill",
      value: renderFileLink(data?.beneficiary?.electricity_bill_attachment),
    },
    {
      label: "GPS Location (Geo Tag)",
      value: renderFileLink(data?.beneficiary?.geo_tag),
    },
    {
      label: "Purchase Proof",
      value: renderFileLink(data?.purchase_details?.purchase_proof),
    },
    {
      label: "Bill of Lading",
      value: renderFileLink(data?.purchase_details?.bill_of_lading),
    },
    {
      label: "Bill of Entry",
      value: renderFileLink(data?.purchase_details?.bill_of_entry),
    },
    {
      label: "Detailed Project Report",
      value: renderFileLink(data?.purchase_details?.dpr_copy),
    },
    {
      label: "Investment Proof",
      value: renderFileLink(data?.purchase_details?.investment_proof),
    },
    {
      label: "Loan Account Copy",
      value: renderFileLink(data?.purchase_details?.loan_account_copy),
    },
    {
      label: "Bank Passbook",
      value: renderFileLink(data?.bank_details?.bank_passbook),
    },
    {
      label: "Export Oriented Copy",
      value: renderFileLink(data?.additional_details?.export_oriented_copy),
    },
    {
      label: "Marketing Tieup Details",
      value: renderFileLink(data?.additional_details?.marketing_tieup_details),
    },
    {
      label: "Turnover Proof",
      value: renderFileLink(data?.additional_details?.turnover_proof),
    },
    {
      label: "Applicant Signature",
      value: renderFileLink(data?.declaration?.applicant_signature),
    },
    {
      label: "Undertaking certificate for not selling",
      value: renderFileLink(data?.purchase_details?.purchase_detail_bond),
    },
    {
      label: "Declaration Document",
      value: renderFileLink(data?.declaration?.declaration_document),
    },
    {
      label: "Rental/Lease Agreement",
      value: renderFileLink(data?.beneficiary?.rental_lease_upload),
    },
    {
      label: "Other Valid Documents",
      value: renderFileLink(data?.beneficiary?.other_valid_documents),
    },
    {
      label: "Scheme Documents",
      value: renderFileLink(data?.beneficiary?.scheme_documents),
    },
    ...(
      data?.purchase_details?.loom_purchases?.map((loom, index) => [
        {
          label: `Loom ${index + 1} Invoice`,
          value: renderFileLink(loom?.invoice_copy),
        },
        {
          label: `Loom ${index + 1} Machine Photo`,
          value: renderFileLink(loom?.machine_photo),
        },
      ]) || []
    ).flat(),
  ];

  const basicInfo = [
    { label: "Applicant Name", value: data?.applicant_name },
    { label: "Organization Type", value: data?.membership },
    { label: "Name of Proprietor/Partners", value: data?.name_of_proprietor },
    { label: "Father/Spouse Name", value: data?.father_name },
    { label: "Date of Birth", value: formatDate(data?.date_of_birth) },
    { label: "Age", value: data?.age },
    {
      label: "Date of Incorporation",
      value: formatDate(data?.date_of_incorporation),
    },
    { label: "Gender", value: data?.gender },
    {
      label: "Community",
      value: data?.community_info?.name || "-",
    },
    { label: "Contact Number", value: data?.contact_number },
    { label: "Email", value: data?.email },
    { label: "Web Address", value: data?.web_address },
    { label: "Aadhaar Number", value: data?.aadhaar_number },
    { label: "Residential Address", value: data?.residential_address },
    { label: "City", value: data?.city },
    {
      label: "District",
      value: data?.district?.name || data?.district_info?.name || "-",
    },
    { label: "State", value: data?.state },
    { label: "Residential Pincode", value: data?.residential_pincode },
    { label: "PAN Number", value: data?.pan_number },
    { label: "GST Number", value: data?.gst_number },
  ];

  const beneficiaryInfo = [
    { label: "Loom Shed Name", value: data?.beneficiary?.loom_shed_name },
    { label: "Loom Shed Address", value: data?.beneficiary?.loom_shed_address },
    { label: "Loom Shed Pincode", value: data?.beneficiary?.loom_shed_pincode },
    { label: "EB Service Number", value: data?.beneficiary?.eb_service_number },
    { label: "Free EB Availed", value: data?.beneficiary?.free_eb_availed },
    // { label: "Units Used", value: data?.beneficiary?.units_used },
    {
      label: "Electricity Bill Owner Name",
      value: data?.beneficiary?.electricity_bill_owner_name,
    },
    { label: "Has Other Schemes", value: data?.beneficiary?.has_other_schemes },
    {
      label: "Other State Gov Scheme",
      value: data?.beneficiary?.other_state_gov_scheme,
    },
    // { label: "Other Scheme Details", value: data?.beneficiary?.other_scheme_details },
    { label: "Premises Type", value: data?.beneficiary?.premises_type },
    {
      label: "Variety Produced",
      value: Array.isArray(data?.beneficiary?.variety_produced)
        ? data.beneficiary.variety_produced.join(", ")
        : data?.beneficiary?.variety_produced || "-",
    },
    // { label: "Other Variety", value: data?.beneficiary?.other_variety },
    { label: "Rapier Looms", value: data?.beneficiary?.rapier_looms },
  ];

  const bankInfo = [
    {
      label: "Account Holder Name",
      value: data?.bank_details?.account_holder_name,
    },
    { label: "Account Number", value: data?.bank_details?.account_no },
    { label: "Account Type", value: data?.bank_details?.account_type },
    {
      label: "Account Operated By",
      value: data?.bank_details?.account_operated_by,
    },
    { label: "Bank Name", value: data?.bank_details?.bank_name },
    { label: "Bank Address", value: data?.bank_details?.bank_address },
    { label: "Branch", value: data?.bank_details?.branch },
    { label: "Bank District", value: data?.bank_details?.bank_district },
    { label: "IFSC Code", value: data?.bank_details?.ifsc_code },
    { label: "MICR Code", value: data?.bank_details?.micr_code },
  ];

  const purchaseInfo = [
    {
      label: "Number of Looms to Purchase",
      value: data?.purchase_details?.no_of_loom_to_purchase,
    },
    {
      label: "Already Purchase",
      value: data?.purchase_details?.already_purchase,
    },
    ...(data?.purchase_details?.already_purchase === "Yes"
      ? [
          {
            label: "Basic Cost Machinery",
            value: formatCurrency(data?.purchase_details?.basic_cost_machinery),
          },
          {
            label: "Basic Cost Accessories",
            value: formatCurrency(
              data?.purchase_details?.basic_cost_accessories
            ),
          },
          {
            label: "Custom Duty",
            value: formatCurrency(data?.purchase_details?.custom_duty),
          },
          {
            label: "Total Investment",
            value: formatCurrency(data?.purchase_details?.total_investment),
          },
          {
            label: "Total Loan Applied",
            value: formatCurrency(data?.purchase_details?.total_loan_applied),
          },
          {
            label: "Total Loan Released",
            value: formatCurrency(data?.purchase_details?.total_loan_released),
          },
          {
            label: "Loan Release Date",
            value: formatDate(data?.purchase_details?.loan_release_date),
          },
          { label: "Loan Period", value: data?.purchase_details?.loan_period },
          {
            label: "Promoters Contribution",
            value: formatCurrency(
              data?.purchase_details?.promoters_contribution
            ),
          },
          {
            label: "Promoters Contribution %",
            value: data?.purchase_details?.promoters_contribution_percent,
          },
          {
            label: "Shared with Other Banks",
            value: data?.purchase_details?.shared_with_other_banks,
          },
          {
            label: "Loan Sanction Date",
            value: formatDate(data?.purchase_details?.loan_sanction_date),
          },
          {
            label: "Machinery Imported/Indigenous",
            value: data?.purchase_details?.machinery_imported_indigenous,
          },
          {
            label: "Loan Application Date",
            value: formatDate(data?.purchase_details?.loan_application_date),
          },
          {
            label: "Loan Sanction by Authority Date",
            value: formatDate(
              data?.purchase_details?.loan_sanction_by_authority_date
            ),
          },
          {
            label: "Repayment Start Date",
            value: formatDate(data?.purchase_details?.repayment_start_date),
          },
          {
            label: "Term Loan Account Number",
            value: data?.purchase_details?.term_loan_account_number,
          },
          {
            label: "Interest Rate %",
            value: data?.purchase_details?.interest_rate,
          },
          {
            label: "Interest Charged Per Year",
            value: formatCurrency(
              data?.purchase_details?.interest_charged_per_year
            ),
          },
          {
            label: "Amount Incurred",
            value: formatCurrency(data?.purchase_details?.amount_incurred),
          },
        ]
      : []),
  ];

  const alreadyPurchaseInfo = [
    {
      label: "Basic Cost Machinery",
      value: formatCurrency(data?.purchase_details?.basic_cost_machinery),
    },
    {
      label: "Basic Cost Accessories",
      value: formatCurrency(data?.purchase_details?.basic_cost_accessories),
    },
    {
      label: "Custom Duty",
      value: formatCurrency(data?.purchase_details?.custom_duty),
    },
    {
      label: "Total Investment",
      value: formatCurrency(data?.purchase_details?.total_investment),
    },
    {
      label: "Total Loan Applied",
      value: formatCurrency(data?.purchase_details?.total_loan_applied),
    },
    {
      label: "Total Loan Released",
      value: formatCurrency(data?.purchase_details?.total_loan_released),
    },
    {
      label: "Loan Release Date",
      value: formatDate(data?.purchase_details?.loan_release_date),
    },
    { label: "Loan Period", value: data?.purchase_details?.loan_period },
    {
      label: "Promoters Contribution",
      value: formatCurrency(data?.purchase_details?.promoters_contribution),
    },
    {
      label: "Promoters Contribution %",
      value: data?.purchase_details?.promoters_contribution_percent,
    },
    {
      label: "Shared with Other Banks",
      value: data?.purchase_details?.shared_with_other_banks,
    },
    {
      label: "Loan Sanction Date",
      value: formatDate(data?.purchase_details?.loan_sanction_date),
    },
    {
      label: "Machinery Imported/Indigenous",
      value: data?.purchase_details?.machinery_imported_indigenous,
    },
    {
      label: "Loan Application Date",
      value: formatDate(data?.purchase_details?.loan_application_date),
    },
    {
      label: "Loan Sanction by Authority Date",
      value: formatDate(
        data?.purchase_details?.loan_sanction_by_authority_date
      ),
    },
    {
      label: "Repayment Start Date",
      value: formatDate(data?.purchase_details?.repayment_start_date),
    },
    {
      label: "Term Loan Account Number",
      value: data?.purchase_details?.term_loan_account_number,
    },
    {
      label: "Interest Rate %",
      value: data?.purchase_details?.interest_rate,
    },
    {
      label: "Interest Charged Per Year",
      value: formatCurrency(data?.purchase_details?.interest_charged_per_year),
    },
    {
      label: "Amount Incurred",
      value: formatCurrency(data?.purchase_details?.amount_incurred),
    },
  ];

  const purchaseDetails = [
    { column: "S.No", value: "" },
    { column: "Loom Name", value: "loom_name" },
    { column: "Serial Number", value: "serial_number" },
    { column: "Purchase Date", value: "purchase_date", is_date: true },
    { column: "Machinery Type", value: "machinery_type" },
    { column: "Make & Model", value: "make_model" },
    { column: "Country of Origin if Imported", value: "country_of_origin" },
    { column: "Machine Photo", value: "machine_photo", is_upload: true },
    { column: "Invoice Copy", value: "invoice_copy", is_upload: true },
  ];

  const additionalDetails = [
    {
      label: "Tie-up Arrangement Made",
      value: data?.additional_details?.is_tie_up_made || "-",
    },
    {
      label: "Type of Tie-up",
      value: Array.isArray(data?.additional_details?.tie_up_types)
        ? data.additional_details.tie_up_types.join(", ")
        : data?.additional_details?.tie_up_types || "-",
    },
    {
      label: "Export Oriented Production",
      value:
        data?.additional_details?.export_oriented === "yes"
          ? "Yes"
          : "No" || "-",
    },
    {
      label: "Type of Product Exported",
      value: data?.additional_details?.type_of_product_exported || "-",
    },
    {
      label: "Source of Raw Material",
      value: data?.additional_details?.backward_raw_material_source || "-",
    },
    {
      label: "Availability of Labour",
      value: data?.additional_details?.backward_labour_availability || "-",
    },
    {
      label: "Annual Turnover (₹)",
      value: formatCurrency(data?.additional_details?.turnover) || "-",
    },
    {
      label: "Cluster Approach",
      value: data?.additional_details?.cluster_approach || "-",
    },
    {
      label: "Willingness to Switch to Commercial Production",
      value:
        data?.additional_details?.willingness_to_switch === "yes"
          ? "Yes"
          : "No" || "-",
    },
    {
      label: "Willingness Description",
      value: data?.additional_details?.willingness_description || "-",
    },
    {
      label: "Opt-out from Scheme",
      value: data?.additional_details?.is_opt_out_from_scheme || "-",
    },
    {
      label: "Entrepreneur Type",
      value: data?.additional_details?.entrepreneur_type || "-",
    },
    {
      label: "Employment Generation",
      value: data?.additional_details?.employment_generation || "-",
    },
    {
      label: "Value Added Varieties",
      value: Array.isArray(data?.additional_details?.value_added_variety)
        ? data.additional_details.value_added_variety.join(", ")
        : data?.additional_details?.value_added_variety || "-",
    },
    {
      label: "Other Variety Specification",
      value: data?.additional_details?.other_variety_specify || "-",
    },
  ];

  const priorityFactors = [
    {
      label: "Whether Engaged in export oriented production",
      value: data?.export_oriented || "-",
    },
    {
      label: "Whether Forward or Backward Integrated",
      value: data?.forward_backward_integration || "-",
    },
    {
      label: " Source of Raw material",
      value: data?.backward_raw_material_source || "-",
    },
    {
      label: "Availability of labour",
      value: data?.backward_labour_availability || "-",
    },
    {
      label: "Loom Machineries",
      value: data?.backward_looms_machineries || "-",
    },
    {
      label: "Marketing Tieup Arrangement Details to be furnished",
      value: data?.forward_marketing_tieup_details || "-",
    },
    {
      label: "What is the Turnover",
      value: formatCurrency(data?.turnover) || "-",
    },
    { label: "Cluster Approach", value: data?.cluster_approach || "-" },
    {
      label: "Cluster Approach Number of Loom",
      value: data?.cluster_approach_no_of_looms || "-",
    },
    {
      label: "Willingness to switch over commercial production and innovations",
      value: data?.willingness_to_switch || "-",
    },
    {
      label: "Willingness Description",
      value: data?.willingness_description || "-",
    },
    {
      label: "Is Opt out from Scheme",
      value: data?.is_opt_out_from_scheme || "-",
    },
    {
      label: "Whether the applicant is a New Entrepreneur",
      value: data?.entrepreneur_type || "-",
    },
    {
      label: "What is the quantum of Employment generation",
      value: data?.employment_generation || "-",
    },
    {
      label: " Whether Engaged in Value added varieties",
      value: Array.isArray(data?.value_added_variety)
        ? data.value_added_variety.join(", ")
        : data?.value_added_variety || "-",
    },
  ];

  const attachedFiles = [
    { label: "Photo", value: data?.photo },
    { label: "PAN Copy", value: data?.pan_copy },
    { label: "GST Copy", value: data?.gst_copy },
    {
      label: "MSME Certificate",
      value: data?.msme_certificate,
    },
    {
      label: "Incorporation Certificate",
      value: data?.incorporation_certificate,
    },
    { label: "Detailed Project Report Copy", value: data?.dpr_copy },
    {
      label: "Loan Sanction Letter",
      value: data?.loan_sanction_letter,
    },
    {
      label: "Machinery Photo",
      value: data?.machinery_photo,
    },
    {
      label: "Purchase Proof",
      value: data?.purchase_proof,
    },
    {
      label: "Origin Certificate",
      value: data?.origin_certificate,
    },
    {
      label: "Bill of Lading",
      value: data?.bill_of_lading,
    },
    {
      label: "Bill of Entry",
      value: data?.bill_of_entry,
    },
    {
      label: "Investment Proof",
      value: data?.investment_proof,
    },
    {
      label: "Loan Account Copy",
      value: data?.loan_account_copy,
    },
    {
      label: "Bank Statement",
      value: data?.bank_statement,
    },
    {
      label: "Scheme Documents",
      value: data?.scheme_documents,
    },
    {
      label: "Applicant Signature",
      value: data?.applicant_signature,
    },
    {
      label: "Export Oriented Copy",
      value: data?.export_oriented_copy,
    },
    {
      label: "Cluster Approach Copy",
      value: data?.cluster_approach_copy,
    },
  ];

  const fieldVisitRemark = data?.remarks?.find(
    (remark) => remark.status === "field_visit_completed"
  );

  const afterPurchaseFieldVisitRemark = data?.remarks?.find(
    (remark) => remark.status === "after_purchase_field_visit_completed"
  );

  const districtOfficerApprove = data?.remarks?.find(
    (remark) => remark.status === "district_officer_approve"
  );

  const districtOfficerReject = data?.remarks?.find(
    (remark) => remark.status === "district_officer_reject"
  );

  const committeeApprove = data?.remarks?.find(
    (remark) => remark.status === "committee_approve"
  );

  const committeeReject = data?.remarks?.find(
    (remark) => remark.status === "committee_reject"
  );

  const handleDownload = (fileUrl) => {
    if (!fileUrl) return;

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const openInNewTab = (fileUrl) => {
    if (!fileUrl) return;
    window.open(fileUrl, "_blank");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">
          New Shuttleless Application Details
        </h1>
        <div className="flex gap-2">
          <Button asChild variant="default">
            <Link to="/submissions">Back</Link>
          </Button>
        </div>
      </div>
      <div className="flex items-center justify-end">
        <div className="flex gap-2">
          {data?.status == "field_visit_completed" && (
            <Button
              variant="default"
              onClick={() => handleDownload(data?.provisional_sanction_letter)}
            >
              Download Provisional Sanction Letter
            </Button>
          )}
          <Button
            variant="default"
            onClick={() => handleDownload(data?.application_url)}
          >
            Download Application
          </Button>
          <Button
            variant="default"
            onClick={() => handleDownload(data?.application_uploads)}
          >
            Download Application Attachments
          </Button>
          {role == "district_admin" &&
            data?.status != "district_officer_approve" &&
            data?.status != "district_officer_reject" &&
            data?.status != "committee_approve" &&
            data?.status != "committee_reject" && (
              <Button variant="destructive" onClick={() => openRejectModal()}>
                Reject Application
              </Button>
            )}
        </div>
      </div>

      {/* Application Overview */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            <span>Application Overview</span>
          </CardTitle>
        </CardHeader>
        <CardContent style={{ marginTop: "15px" }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <p style={{ fontSize: "20px" }}>
              <strong>Application Number:</strong> {data?.application_no}
            </p>
            <p>
              <strong>Status:</strong> <StatusBadge status={data?.status} />
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Review Sections */}
      <ReviewSection
        title="Personal Details"
        data={basicInfo}
        main_data={data}
        tabName="company"
      />
      <ReviewSection
        title="Beneficiary and Loom Details"
        data={beneficiaryInfo}
        main_data={data}
        tabName="products"
      />
      <ReviewSection
        title="Bank Details"
        data={bankInfo}
        main_data={data}
        tabName="machinery"
      />
      <ReviewSection
        title="Additional Info"
        data={additionalDetails}
        main_data={data}
        tabName="bank_name"
      />
      <ReviewSection
        title="Purchase Details"
        data={purchaseInfo}
        main_data={data}
        tabName="purchase_detail"
        tableData={
          data?.purchase_details?.already_purchase === "Yes" && (
            <DynamicReviewTable
              title={"Invoice Details"}
              columns={purchaseDetails}
              rows={data?.purchase_details?.loom_purchases ?? []}
            />
          )
        }
      />
      <FileReviewSection title="Uploaded Files" files={uploadedFiles} />

      {view_page && (
        <Card className="bg-white">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>Declaration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4" style={{ marginTop: "20px" }}>
            <div className="flex items-center space-x-2">
              {data?.declaration?.terms_accepted ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2" />{" "}
                  <p className="font-semibold">
                    User Has Accepted the declaration
                  </p>
                </>
              ) : (
                <>
                  <ShieldCloseIcon className="h-4 w-4 text-red-600 mr-2" />{" "}
                  <p className="font-semibold">
                    User Has Not Accepted the declaration
                  </p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {(role == "district_admin" || role == "admin") && (
        <>
          <Card className="bg-white p-3">
            <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
              <CardTitle>District Officer Field Inspection</CardTitle>
            </CardHeader>
            <CardContent>
              {data?.status &&
              data?.status === "submitted_for_provisional_sanction" &&
              role == "district_admin" ? (
                <FieldInspectionForm
                  formType="new_shuttleless"
                  tabsOrder={tabsOrder}
                  form_id={data?.id}
                  loom_details={
                    data?.purchase_details?.already_purchase === "Yes" &&
                    data?.purchase_details?.loom_purchases
                  }
                  setData={setData}
                  uploads_array={uploads_array}
                  application={data}
                />
              ) : fieldVisitRemark ? (
                <div className="space-y-6 mt-4">
                  {/* Summary */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <h3 className="font-semibold text-green-900">
                        Field Inspection Completed
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 mb-3">Inspected By</p>
                        <p className="font-medium">
                          {fieldVisitRemark.user_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-3">Inspection Date</p>
                        <p className="font-medium">
                          {new Date(
                            fieldVisitRemark.remarks_date
                          ).toLocaleDateString("en-IN", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-3">GPS Location</p>
                        <a
                          href={fieldVisitRemark?.geo_tag}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Provisional Sanction looms */}
                  {fieldVisitRemark.looms_for_sanction &&
                    fieldVisitRemark.looms_for_sanction.length > 0 && (
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3">
                          Looms recommended for provisional sanction
                        </h4>
                        <div className="bg-gray-50 p-4 rounded border">
                          <div className="flex flex-wrap gap-5">
                            {fieldVisitRemark.looms_for_sanction.map((item) => (
                              <span
                                key={item.id}
                                className="inline-flex items-center px-4 py-2 rounded-sm text-lg font-medium bg-blue-100 text-blue-800 border border-blue-200"
                              >
                                {item.loom?.loom_name ||
                                  `Loom ID: ${item.loom_id}`}
                              </span>
                            ))}
                          </div>

                          {/* Optional: Descriptive text if needed */}
                          {!fieldVisitRemark.looms_for_sanction.length && (
                            <p className="text-sm text-gray-500 italic">
                              No looms recommended.
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                  {/* Tab Checklist */}
                  {fieldVisitRemark.tabs &&
                    fieldVisitRemark.tabs.length > 0 && (
                      <div className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-semibold">
                            Tab Verification Status
                          </h4>
                          <span className="text-sm text-gray-600">
                            {
                              fieldVisitRemark.tabs.filter(
                                (t) => t.status === 1
                              ).length
                            }{" "}
                            / {fieldVisitRemark.tabs.length} Completed
                          </span>
                        </div>
                        <div className="space-y-2">
                          {fieldVisitRemark.tabs.map((tab) => {
                            const tabName =
                              tabsOrder?.find((t) => t.id === tab.tab_name)
                                ?.name || tab.tab_name.toUpperCase();
                            const isCompleted = tab.status === 1;
                            return (
                              <div
                                key={tab.tab_name}
                                className={`flex items-center justify-between p-3 border rounded ${
                                  isCompleted
                                    ? "bg-green-50 border-green-200"
                                    : "bg-gray-50 border-gray-200"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {isCompleted ? (
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <Clock className="h-4 w-4 text-gray-400" />
                                  )}
                                  <span
                                    className={`text-sm font-medium ${
                                      isCompleted
                                        ? "text-green-800"
                                        : "text-gray-600"
                                    }`}
                                  >
                                    {tabName}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-600">
                                  {isCompleted ? "Verified" : "Pending"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  {/* Inspection Remarks */}
                  {fieldVisitRemark.description && (
                    <div className="border rounded-lg p-4">
                      <h4 className="font-semibold mb-2">Inspection Remarks</h4>
                      <div className="bg-gray-50 p-3 rounded border">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {fieldVisitRemark.description}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Uploaded Documents */}
                  {fieldVisitRemark.files &&
                    fieldVisitRemark.files.length > 0 && (
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3">
                          Uploaded Documents
                        </h4>
                        <div className="space-y-2">
                          {fieldVisitRemark.files.map((file, index) => (
                            <div
                              key={file.id}
                              className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                            >
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-blue-600" />
                                <span className="text-sm font-medium">
                                  {file.upload_type || `Document ${index + 1}`}
                                </span>
                              </div>
                              <a
                                href={file.upload_copy}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              >
                                <Download className="h-4 w-4" />
                                Download
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              ) : (
                <p className="text-gray-500">
                  No field inspection record found
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white p-3">
            <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
              <CardTitle>Provisional Sanction</CardTitle>
              {data?.status == "field_visit_completed" && (
                <Button
                  variant="default"
                  onClick={() =>
                    handleDownload(data?.provisional_sanction_letter)
                  }
                >
                  Download Provisional Sanction Letter
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {data?.status &&
              data?.status === "field_visit_completed" &&
              role == "district_admin" ? (
                <ProvisionalSanctionForm
                  formType="new_shuttleless"
                  form_id={data?.id}
                  setData={setData}
                  application={data}
                />
              ) : data?.sanction ? (
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Provisional Sanction Order No.
                      </label>
                      <p className="text-base font-semibold">
                        {data.sanction.sanction_order_no}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Provisional Sanction Order Date
                      </label>
                      <p className="text-base font-semibold">
                        {new Date(
                          data.sanction.sanction_order_date
                        ).toLocaleDateString()}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Provisional Sanction Amount
                      </label>
                      <p className="text-base font-semibold">
                        ₹{data.sanction.sanction_order_amount}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        District Officer
                      </label>
                      <p className="text-base font-semibold">
                        {data.sanction.district_officer?.name}
                      </p>
                    </div>
                  </div>

                  {data.sanction.description && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Description
                      </label>
                      <p className="text-base">{data.sanction.description}</p>
                    </div>
                  )}

                  {data.sanction.sanctioned_copy && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Provisional Sanctioned Copy
                      </label>
                      <div className="mt-2">
                        <a
                          href={data.sanction.sanctioned_copy}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                          <svg
                            className="w-4 h-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          Download Sanction Copy
                        </a>
                      </div>
                    </div>
                  )}

                  {data.sanction.is_user_accept !== null && (
                    <div className="pt-4 border-t">
                      <label className="text-sm font-medium text-gray-500">
                        User Acceptance Status
                      </label>
                      <p className="text-base font-semibold">
                        {data.sanction.is_user_accept ? (
                          <span className="text-green-600">Accepted</span>
                        ) : (
                          <span className="text-red-600">Not Accepted</span>
                        )}
                      </p>
                      {data.sanction.user_accept_date && (
                        <p className="text-sm text-gray-500 mt-1">
                          on{" "}
                          {new Date(data?.sanction.user_accept_date)
                            .toLocaleDateString("en-GB")
                            .replace(/\//g, " - ")}
                          {"  "}
                          {new Date(
                            data?.sanction.user_accept_date
                          ).toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">No sanction details available</p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {data?.sanction?.status == "provisional_sanction_recommended" &&
        data?.purchase_details?.already_purchase === "No" &&
        data?.purchase_details?.basic_cost_machinery && (
          <ReviewSection
            title="After Sanction Purchase Details"
            data={alreadyPurchaseInfo}
            main_data={data}
            tabName="purchase_detail"
            tableData={
              <DynamicReviewTable
                title={"Loom Details"}
                columns={purchaseDetails}
                rows={data?.purchase_details?.loom_purchases ?? []}
              />
            }
          />
        )}

      {data?.status &&
        data?.status === "submitted_for_approval" &&
        data?.purchase_details?.already_purchase === "No" && (
          <Card>
            <CardHeader>
              <CardTitle>After Purchase Declaration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4 border-t pt-4">
                {data?.purchase_declaration?.terms_accepted ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />{" "}
                    <p className="font-semibold">
                      User Has Accepted the declaration
                    </p>
                  </>
                ) : (
                  <>
                    <ShieldCloseIcon className="h-4 w-4 text-red-600 mr-2" />{" "}
                    <p className="font-semibold">
                      User Has Not Accepted the declaration
                    </p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

      {(role == "district_admin" || role == "admin") &&
        data?.purchase_details?.already_purchase == "No" && (
          <Card className="bg-white p-3">
            <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
              <CardTitle>
                District Officer After Purchase Field Inspection
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.status &&
              data?.status == "submitted_for_approval" &&
              role == "district_admin" ? (
                <FieldInspectionForm
                  formType="new_shuttleless"
                  tabsOrder={afterSanctionTabsOrder}
                  after_purchase={true}
                  form_id={data?.id}
                  setData={setData}
                  uploads_array={uploads_array}
                  application={data}
                />
              ) : afterPurchaseFieldVisitRemark ? (
                <div className="space-y-6 mt-4">
                  {/* Summary */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <h3 className="font-semibold text-green-900">
                        After Purchase Field Inspection Completed
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 mb-3">Inspected By</p>
                        <p className="font-medium">
                          {afterPurchaseFieldVisitRemark.user_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-3">Inspection Date</p>
                        <p className="font-medium">
                          {new Date(
                            afterPurchaseFieldVisitRemark.remarks_date
                          ).toLocaleDateString("en-IN", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-3">GPS Location</p>
                        <a
                          href={afterPurchaseFieldVisitRemark?.geo_tag}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Tab Checklist */}
                  {afterPurchaseFieldVisitRemark.tabs &&
                    afterPurchaseFieldVisitRemark.tabs.length > 0 && (
                      <div className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-semibold">
                            Tab Verification Status
                          </h4>
                          <span className="text-sm text-gray-600">
                            {
                              afterPurchaseFieldVisitRemark.tabs.filter(
                                (t) => t.status === 1
                              ).length
                            }{" "}
                            / {afterPurchaseFieldVisitRemark.tabs.length}{" "}
                            Completed
                          </span>
                        </div>
                        <div className="space-y-2">
                          {afterPurchaseFieldVisitRemark.tabs.map((tab) => {
                            const tabName =
                              tabsOrder?.find((t) => t.id === tab.tab_name)
                                ?.name || tab.tab_name.toUpperCase();
                            const isCompleted = tab.status === 1;
                            return (
                              <div
                                key={tab.tab_name}
                                className={`flex items-center justify-between p-3 border rounded ${
                                  isCompleted
                                    ? "bg-green-50 border-green-200"
                                    : "bg-gray-50 border-gray-200"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {isCompleted ? (
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <Clock className="h-4 w-4 text-gray-400" />
                                  )}
                                  <span
                                    className={`text-sm font-medium ${
                                      isCompleted
                                        ? "text-green-800"
                                        : "text-gray-600"
                                    }`}
                                  >
                                    {tabName}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-600">
                                  {isCompleted ? "Verified" : "Pending"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  {/* Inspection Remarks */}
                  {afterPurchaseFieldVisitRemark.description && (
                    <div className="border rounded-lg p-4">
                      <h4 className="font-semibold mb-2">Inspection Remarks</h4>
                      <div className="bg-gray-50 p-3 rounded border">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {afterPurchaseFieldVisitRemark.description}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Uploaded Documents */}
                  {afterPurchaseFieldVisitRemark.files &&
                    afterPurchaseFieldVisitRemark.files.length > 0 && (
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3">
                          Uploaded Documents
                        </h4>
                        <div className="space-y-2">
                          {afterPurchaseFieldVisitRemark.files.map(
                            (file, index) => (
                              <div
                                key={file.id}
                                className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                              >
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-blue-600" />
                                  <span className="text-sm font-medium">
                                    {file.upload_type ||
                                      `Document ${index + 1}`}
                                  </span>
                                </div>
                                <a
                                  href={file.upload_copy}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                >
                                  <Download className="h-4 w-4" />
                                  Download
                                </a>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                </div>
              ) : (
                <p className="text-gray-500">
                  No field inspection record found
                </p>
              )}
            </CardContent>
          </Card>
        )}

      {districtOfficerApprove && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>District Officer Approval</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold text-green-900">
                    Approval Completed
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Approved By</p>
                    <p className="font-medium">
                      {districtOfficerApprove.user_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Approval Date</p>
                    <p className="font-medium">
                      {new Date(districtOfficerApprove.remarks_date)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, " - ")}
                      {"  "}
                      {new Date(
                        districtOfficerApprove.remarks_date
                      ).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Approval Remarks */}
              {districtOfficerApprove.description && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Approval Remarks</h4>
                  <div className="bg-gray-50 p-3 rounded border">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {districtOfficerApprove.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {districtOfficerReject && (
        <Card>
          <CardHeader>
            <CardTitle>District Officer Reject</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">
                    Application Rejected
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Rejected By</p>
                    <p className="font-medium">
                      {districtOfficerReject.user_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Rejected Date</p>
                    <p className="font-medium">
                      {new Date(districtOfficerReject.remarks_date)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, " - ")}
                      {"  "}
                      {new Date(
                        districtOfficerReject.remarks_date
                      ).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Approval Remarks */}
              {districtOfficerReject.description && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Rejected Remarks</h4>
                  <div className="bg-gray-50 p-3 rounded border">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {districtOfficerReject.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {committeeApprove && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>Committee Approval</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold text-green-900">
                    Approval Completed
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Approved By</p>
                    <p className="font-medium">{committeeApprove.user_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Approval Date</p>
                    <p className="font-medium">
                      {new Date(committeeApprove.remarks_date)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, " - ")}
                      {"  "}
                      {new Date(
                        committeeApprove.remarks_date
                      ).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Approval Remarks */}
              {committeeApprove.description && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Approval Remarks</h4>
                  <div className="bg-gray-50 p-3 rounded border">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {committeeApprove.description}
                    </p>
                  </div>
                </div>
              )}

              {committeeApprove.approve_file && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Approval Document</h4>
                  <div className="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium">Approval File</span>
                    </div>
                    <button
                      onClick={() =>
                        openInNewTab(committeeApprove.approve_file)
                      }
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {committeeReject && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>Committee Reject</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">
                    Application Rejected
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Rejected By</p>
                    <p className="font-medium">{committeeReject.user_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Rejected Date</p>
                    <p className="font-medium">
                      {new Date(committeeReject.remarks_date)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, " - ")}
                      {"  "}
                      {new Date(
                        committeeReject.remarks_date
                      ).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Approval Remarks */}
              {committeeReject.description && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Rejected Remarks</h4>
                  <div className="bg-gray-50 p-3 rounded border">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {committeeReject.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {role == "district_admin" &&
        ((data?.purchase_details?.already_purchase === "No" &&
          afterPurchaseFieldVisitRemark &&
          data?.status == "after_purchase_field_visit_completed") ||
          (data?.purchase_details?.already_purchase === "Yes" &&
            data?.status == "submitted_for_approval")) && (
          <Card className="bg-white p-3">
            <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
              <CardTitle>District Officer Approval</CardTitle>
            </CardHeader>
            <CardContent className="flex justify-start gap-4 mt-10">
              <Button
                disabled={isSubmitting}
                variant="success"
                onClick={() => openApproveModal()}
                className="py-5"
              >
                Approve the Application and Send for Final Committee
              </Button>
              <Button
                disabled={isSubmitting}
                variant="destructive"
                onClick={() => openRejectModal()}
              >
                Reject the Application
              </Button>
            </CardContent>
          </Card>
        )}

      {role == "admin" && data?.status == "district_officer_approve" && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>Committee Approval</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-start gap-4 mt-10">
            <Button
              disabled={isSubmitting}
              variant="success"
              onClick={() => openApproveModal()}
              className="py-5"
            >
              Approve the Application
            </Button>
            <Button
              disabled={isSubmitting}
              variant="destructive"
              onClick={() => openRejectModal()}
            >
              Reject the Application
            </Button>
          </CardContent>
        </Card>
      )}

      {role == "admin" && data?.status == "committee_approve" && (
        <Card className="bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle>Reject Application</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-start gap-4 mt-10">
            <Button
              disabled={isSubmitting}
              variant="destructive"
              onClick={() => openRejectModal()}
            >
              Reject the Application
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={approveModalOpen} onOpenChange={setApproveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Approval</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            Are you sure you want to approve this application. Once done the
            application will be sent to the committee
          </div>
          <div className="py-4 space-y-2">
            <p>Enter remarks:</p>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks..."
            />
          </div>
          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setApproveModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                "Approve"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Submission</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <p>Enter remarks for rejection:</p>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks..."
            />
          </div>
          <DialogFooter className="flex space-x-2">
            <Button variant="outline" onClick={() => setRejectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={isSubmitting || !remarks.trim()}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                "Reject"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={adminApproveModalOpen}
        onOpenChange={setAdminApproveModalOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Application</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            Are you sure you want to approve this Application. Once done the
            Application will be freezed and a sms will be sent to the concern
            Applicant.
          </div>
          <div className="py-4 space-y-2">
            <p>Provisional Sanction Amount:</p>
            <Input
              value={provisionalSanctionAmount}
              onChange={(e) => setProvisionalSanctionAmount(e.target.value)}
            />
            <p>Approve File</p>
            <Input
              type="file"
              id="approve_file"
              name="approve_file"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setApproveFile(e.target.files[0]);
                }
              }}
            />
            <p className="pt-2">Enter remarks for Approve:</p>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks..."
            />
          </div>
          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setAdminApproveModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              disabled={isSubmitting || !remarks.trim()}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                "Approve"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewShuttlelessDetails;
